import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { isPhoneDevice } from "@/lib/phone";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { PhoneIncoming, PhoneOutgoing } from "lucide-react";
import { toast } from "sonner";

export const fmtDur = (s) => `${Math.floor((s || 0) / 60)}m ${(s || 0) % 60}s`;
// Pages with their own Log Call dialog set this so the global prompt doesn't double-ask.
export const setLogCallOpen = (v) => { window.__ohcLogCallOpen = v; };

// Polls the phone call that just ended for a CRM contact while the desktop CRM is open.
export function useLastPhoneCall(params, active) {
  const [call, setCall] = useState(null);
  const key = JSON.stringify(params);
  useEffect(() => {
    if (!active) { setCall(null); return undefined; }
    setLogCallOpen(true);
    const tick = () => api.get("/mobile/last-call", { params: JSON.parse(key) }).then(({ data }) => { if (data?.id) setCall(data); }).catch(() => {});
    tick();
    const t = setInterval(tick, 2000);
    return () => { clearInterval(t); setLogCallOpen(false); };
  }, [key, active]);
  return call;
}

export default function DesktopCallPrompt() {
  const [call, setCall] = useState(null);
  const [f, setF] = useState({ disposition: "", notes: "", callback_at: "" });
  useEffect(() => {
    if (isPhoneDevice()) return undefined;
    const ping = () => { if (document.visibilityState === "visible") api.post("/mobile/desktop-ping").catch(() => {}); };
    const poll = () => { if (!window.__ohcLogCallOpen && !document.querySelector("[role=dialog]")) api.get("/mobile/pending-outcome").then(({ data }) => { if (data?.id) { setCall(data); setF({ disposition: "", notes: "", callback_at: "" }); } }).catch(() => {}); };
    ping(); poll();
    const a = setInterval(ping, 20000); const b = setInterval(poll, 5000);
    return () => { clearInterval(a); clearInterval(b); };
  }, []);
  if (!call) return null;
  const cb = f.disposition.startsWith("Call Back");
  const save = async () => {
    if (!f.disposition) { toast.error("Select the call result"); return; }
    try {
      await api.post(`/mobile/calls/${call.id}/outcome-web`, { ...f, callback_at: f.callback_at ? new Date(f.callback_at).toISOString() : "" });
      toast.success("Call outcome saved"); setCall(null);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const Icon = call.direction === "Inbound" ? PhoneIncoming : PhoneOutgoing;
  return (
    <Dialog open onOpenChange={(o) => !o && setCall(null)}>
      <DialogContent className="bg-white max-w-md" data-testid="desktop-call-prompt">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Icon className="w-5 h-5 text-[#4F46E5]" />Log Call · {call.student_name || call.mobile}</DialogTitle></DialogHeader>
        <div className="text-sm text-slate-500" data-testid="desktop-call-meta">{call.direction} · {call.mobile} · Duration <b className="text-slate-800">{fmtDur(call.duration)}</b> (from phone)</div>
        <div><Label>Call result</Label>
          <Select value={f.disposition} onValueChange={(v) => setF({ ...f, disposition: v })}>
            <SelectTrigger className="mt-1" data-testid="desktop-call-disp"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent className="bg-white">{call.outcomes.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
          </Select></div>
        {cb && <div><Label>Callback date & time</Label><Input type="datetime-local" className="mt-1" value={f.callback_at} onChange={(e) => setF({ ...f, callback_at: e.target.value })} data-testid="desktop-call-callback" /></div>}
        <div><Label>Note</Label><Textarea rows={3} className="mt-1" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="desktop-call-notes" /></div>
        <div className="flex gap-2 justify-end"><Button variant="outline" onClick={() => setCall(null)} data-testid="desktop-call-skip">Later</Button><Button className="bg-[#4F46E5] hover:bg-[#4338CA]" onClick={save} data-testid="desktop-call-save">Save</Button></div>
      </DialogContent>
    </Dialog>
  );
}
